import React from 'react';

interface DisclaimerPageProps {
  onNavigate?: (path: string, view: string, tab?: string) => void;
}

export const DisclaimerPage: React.FC<DisclaimerPageProps> = ({ onNavigate }) => {
  const handleNav = (path: string, view: string, tab: string = 'water') => {
    if (onNavigate) {
      onNavigate(path, view, tab);
    } else {
      window.location.href = path;
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <article className="bg-white dark:bg-[#1F2937] rounded-3xl p-6 sm:p-10 lg:p-12 space-y-8 shadow-xs border border-slate-200 dark:border-slate-700">
        <header className="border-b border-slate-100 dark:border-slate-700/80 pb-6">
          <span className="text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border border-amber-200/60 dark:border-amber-800/60 inline-block mb-3">
            Engineering &amp; Technical Notice
          </span>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-[#10283D] dark:text-white tracking-tight">
            Engineering Disclaimer
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
            Important information regarding the mathematical basis, calculation approximations, and professional verification requirements of this engineering calculation suite.
          </p>
        </header>

        {/* Callout Notice */}
        <div className="p-5 sm:p-6 rounded-2xl bg-amber-50/70 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/50 space-y-2">
          <h2 className="font-display font-bold text-base text-amber-900 dark:text-amber-200 flex items-center gap-2">
            <span>⚠️</span>
            <span>Preliminary Hydraulic Estimation Notice</span>
          </h2>
          <p className="text-xs sm:text-sm text-amber-900/90 dark:text-amber-200/90 leading-relaxed">
            All tools on PipeSizingCalculator.com are intended strictly for educational, conceptual sizing, and preliminary design estimation purposes. Calculations produced by this website do not constitute stamped engineering documents, certified designs, or formal consulting advice. Never use results for construction or fabrication without independent review by a licensed Professional Engineer.
          </p>
        </div>

        {/* 1. Mathematical Nature of Fluid Calculations */}
        <section className="space-y-4 text-sm sm:text-base text-slate-700 dark:text-slate-300 leading-relaxed">
          <h2 className="font-display font-bold text-xl text-[#10283D] dark:text-white">
            1. Nature of Hydraulic Calculations &amp; Approximations
          </h2>
          <p>
            Fluid dynamics in real-world piping systems involves complex thermodynamic, hydraulic, and mechanical phenomena. The calculators on this website implement established empirical equations and standard industry approximations:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-sm text-slate-700 dark:text-slate-300">
            <li>
              <strong>Darcy-Weisbach Formulation:</strong> Calculates friction loss assuming steady, fully developed, one-dimensional Newtonian fluid flow.
            </li>
            <li>
              <strong>Swamee-Jain Explicit Approximation:</strong> Solves the implicit Colebrook-White equation with an accepted engineering accuracy of approximately &plusmn;1&ndash;2% within its valid Reynolds number range (4,000 &le; Re &le; 10<sup>8</sup>).
            </li>
            <li>
              <strong>Water Property Formulations:</strong> Density (&rho;), dynamic viscosity (&mu;), and kinematic viscosity (&nu;) are computed based on standard temperature approximations at ambient atmospheric pressure (101.325 kPa).
            </li>
            <li>
              <strong>Velocity Guidelines:</strong> CIBSE Guide G, BS EN 806, and ASHRAE velocity caps are provided for general guidance and do not supersede project-specific acoustic specifications or municipal health authority regulations.
            </li>
          </ul>
        </section>

        {/* 2. Impact of Field Variables and Installation Conditions */}
        <section className="space-y-4 text-sm sm:text-base text-slate-700 dark:text-slate-300 leading-relaxed">
          <h2 className="font-display font-bold text-xl text-[#10283D] dark:text-white">
            2. Field Discrepancies &amp; Physical Installation Variables
          </h2>
          <p>
            Theoretical calculations cannot account for the myriad site-specific conditions present in actual building installations. Results can deviate substantially from theoretical models due to:
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
            <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700">
              <h3 className="font-display font-bold text-sm text-[#10283D] dark:text-white mb-1">
                Piping Wall Aging &amp; Scale
              </h3>
              <p className="text-xs text-slate-600 dark:text-slate-300">
                Limescale deposition, tuberculation, internal corrosion, and biofilm accumulation increase wall roughness (&epsilon;) significantly over 5 to 30 years of service life.
              </p>
            </div>

            <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700">
              <h3 className="font-display font-bold text-sm text-[#10283D] dark:text-white mb-1">
                Fittings &amp; Complex Geometry
              </h3>
              <p className="text-xs text-slate-600 dark:text-slate-300">
                Close-coupled elbows, non-standard valves, strainers, and tees create swirling, non-developed velocity profiles that increase equivalent length head loss beyond simple &Sigma;K approximations.
              </p>
            </div>

            <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700">
              <h3 className="font-display font-bold text-sm text-[#10283D] dark:text-white mb-1">
                Water Hammer &amp; Surge
              </h3>
              <p className="text-xs text-slate-600 dark:text-slate-300">
                Rapid closure of solenoid valves, quarter-turn taps, or booster pump trip conditions produce transient shockwaves (Joukowsky pressure surge) requiring specialized acoustic shock arrestors.
              </p>
            </div>

            <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700">
              <h3 className="font-display font-bold text-sm text-[#10283D] dark:text-white mb-1">
                Manufacturing Tolerances
              </h3>
              <p className="text-xs text-slate-600 dark:text-slate-300">
                Actual internal diameters vary within permissible manufacturer schedule tolerances (e.g., &plusmn;10% wall thickness tolerance in ASME/ASTM standards).
              </p>
            </div>
          </div>
        </section>

        {/* 3. Independent Professional Verification Requirement */}
        <section className="space-y-4 text-sm sm:text-base text-slate-700 dark:text-slate-300 leading-relaxed">
          <h2 className="font-display font-bold text-xl text-[#10283D] dark:text-white">
            3. Requirement for Independent Professional Verification
          </h2>
          <p>
            Before implementing any piping sizing, pump head selection, valve authority specification, or velocity limits in real-world commercial, residential, healthcare, or industrial applications:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-sm text-slate-700 dark:text-slate-300">
            <li>
              You must verify all design criteria with a registered, chartered, or licensed <strong>Professional Engineer (PE / CEng)</strong> holding appropriate jurisdiction credentials.
            </li>
            <li>
              You must ensure compliance with all governing regional plumbing and building codes (such as the Uniform Plumbing Code (UPC), International Plumbing Code (IPC), BS EN 806, CIBSE Guide G, or local municipal water authority bylaws).
            </li>
            <li>
              You must verify pipe material ratings against project working pressure, design water temperature, and disinfectant compatibility (e.g., chlorine dioxide or thermal shock disinfection protocols).
            </li>
          </ul>
        </section>

        {/* 4. No Professional Certification Claimed */}
        <section className="space-y-4 text-sm sm:text-base text-slate-700 dark:text-slate-300 leading-relaxed">
          <h2 className="font-display font-bold text-xl text-[#10283D] dark:text-white">
            4. No Certification or Guaranteed Accuracy
          </h2>
          <p>
            PipeSizingCalculator.com makes no representation, warranty, or guarantee that calculation outputs are error-free, exhaustive, or suitable for any specific construction project. We explicitly disclaim all liability for any direct, indirect, or consequential losses, delays, or physical damages resulting from the use of our calculation tools or reliance on the documentation published on this website.
          </p>
        </section>

        {/* Navigation Quick Links */}
        <footer className="pt-6 border-t border-slate-100 dark:border-slate-700/80 flex flex-wrap gap-3 text-xs font-semibold">
          <button
            onClick={() => handleNav('/', 'calculators', 'water')}
            className="px-4 py-2.5 bg-[#087F9C] text-white rounded-xl hover:bg-teal-700 transition-colors"
          >
            Launch Water Pipe Sizing &rarr;
          </button>
          <button
            onClick={() => handleNav('/about-us/', 'about')}
            className="px-4 py-2.5 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 rounded-xl hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
          >
            About Us &rarr;
          </button>
          <button
            onClick={() => handleNav('/terms-and-conditions/', 'terms')}
            className="px-4 py-2.5 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 rounded-xl hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
          >
            Terms and Conditions &rarr;
          </button>
        </footer>
      </article>
    </div>
  );
};
